import type { ValuesPayload, ZAnyConfig } from '@atlaskit/config-common-libs/types';
import type { CustomAttributes, Identifiers } from '@atlaskit/feature-gate-js-client/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { PerimeterType } from '@atlaskit/feature-gate-js-client/types';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { applyPersistedStatsigOverrides } from '@confluence/session-data/entry-points/persistedStatsigOverrides.ts';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { getStatsigEnvironment } from './getStatsigEnvironment.ts';

// https://bitbucket.org/atlassian/feature-flag-service/src/a6adccc26a95818c8128c63e4eb817414793b3c2/src/main/resources/application.yml#lines-386
const TARGET_APP = 'confluence_web';

export async function initializeClientFromValues({
	identifiers,
	environment: confluenceEnvironment,
	clientSdkKey,
	experimentValues,
	customAttributes,
	configCollectionValues,
	localMode,
	disableLocalStorageAndLogging,
}: {
	identifiers: {
		userId: SessionDataType['userId'];
		tenantId: Identifiers['tenantId'];
		orgId?: Identifiers['atlassianOrgId'];
	};
	environment: SessionDataType['environment'] | string;
	clientSdkKey?: string;
	experimentValues?: Record<string, unknown>;
	customAttributes?: CustomAttributes;
	configCollectionValues?: ValuesPayload | ZAnyConfig;
	localMode?: boolean;
	/**
	 * When `true`, disables the underlying `@statsig/js-client`'s localStorage
	 * persistence and event logging by passing `disableLocalStorage` /
	 * `disableAllLogging` to `FeatureGates.initializeFromValues`.
	 *
	 * Intended for SSR runtimes (Bifrost / cc-frontend-ssr) where there is no
	 * real localStorage and no outbound network for `r.statsig.com`. In those
	 * environments the `EventLogger` flushes every queued exposure into the
	 * `_saveFailedLogsToStorage` retry loop on a module-level in-memory shim;
	 */
	disableLocalStorageAndLogging?: boolean;
}): Promise<void> {
	if (FeatureGates.initializeCompleted()) {
		return;
	}

	const { userId, tenantId, orgId } = identifiers;
	try {
		await FeatureGates.initializeFromValues(
			{
				sdkKey: clientSdkKey,
				environment: getStatsigEnvironment(confluenceEnvironment),
				targetApp: TARGET_APP,
				perimeter:
					process.env.MICROS_PERIMETER === 'fedramp-moderate'
						? PerimeterType.FEDRAMP_MODERATE
						: PerimeterType.COMMERCIAL,
				...(localMode ? { localMode: true } : {}),
				// These option names are the @atlaskit/feature-gate-js-client
				// public API; they map to the underlying client's
				// `disableStorage` / `disableLogging` via
				// `migrateInitializationOptions`.
				...(disableLocalStorageAndLogging
					? { disableLocalStorage: true, disableAllLogging: true }
					: {}),
			},
			{
				atlassianAccountId: userId || undefined,
				tenantId,
				atlassianOrgId: orgId,
			},
			customAttributes,
			localMode ? {} : experimentValues,
		);

		applyPersistedStatsigOverrides();

		StatsigConfigurations.initializeFromValues(configCollectionValues || {});
	} catch (error: unknown) {
		if (error instanceof Error) {
			getMonitoringClient().submitError(error, {
				attribution: 'statsig',
			});
		}
	}
}
